import { v4 as uuidv4 } from 'uuid';

// ── System Form Types ───────────────────────────────────────────────────────
// Top-level form types shown in the type picker.
// All consent sub-types (treatment + non-treatment) live under CONSENT and are
// selected via the ConsentTemplateChooser.

export const SYSTEM_FORM_TYPES = [
    {
        value: 'CONSENT',
        label: 'Consent Form',
        description: 'Treatment and non-treatment consent forms with signature',
        icon: 'solar:document-text-bold-duotone',
        color: '#078DEE',
    },
    {
        value: 'MEDICAL_HISTORY',
        label: 'Medical History',
        description: 'Patient medical background and health questionnaire',
        icon: 'solar:heart-pulse-bold-duotone',
        color: '#FF5630',
    },
    {
        value: 'SURVEY',
        label: 'Survey',
        description: 'Patient satisfaction and feedback forms',
        icon: 'solar:chart-2-bold-duotone',
        color: '#855CF8',
    },
    {
        value: 'PATIENT_INTAKE',
        label: 'Patient Intake',
        description: 'New patient registration and onboarding',
        icon: 'solar:user-plus-bold-duotone',
        color: '#00AB55',
    },
    {
        value: 'REFERRAL',
        label: 'Referral',
        description: 'Patient referral documentation and notes',
        icon: 'solar:share-bold-duotone',
        color: '#FFAB00',
    },
    {
        value: 'TREATMENT_PLAN',
        label: 'Treatment Plan',
        description: 'Proposed treatment details and patient agreement',
        icon: 'solar:stethoscope-bold-duotone',
        color: '#FF3030',
    },
    {
        value: 'OTHER',
        label: 'Other',
        description: 'Custom purpose forms',
        icon: 'solar:document-bold-duotone',
        color: '#637381',
    },
];

/**
 * Returns the config object for a given form type value.
 * Falls back to the OTHER config if the type is not found.
 */
export const getFormTypeConfig = (typeValue) => {
    const found = SYSTEM_FORM_TYPES.find((t) => t.value === typeValue);
    return found || SYSTEM_FORM_TYPES.find((t) => t.value === 'OTHER');
};

/**
 * Friendly display labels for condition operators.
 * Mirrors the OPERATORS array in ConsentConditionEditor — kept here so other
 * components (e.g. a read-only condition summary) can reuse the same labels.
 */
export const OPERATOR_LABELS = {
    equals: 'is',
    not_equals: 'is not',
    contains: 'includes',
    not_contains: "doesn't include",
    in: 'is one of',
    not_in: 'is not one of',
    starts_with: 'starts with',
    is_empty: 'is not answered',
    is_not_empty: 'has been answered',
};

// ── Form Modes ─────────────────────────────────────────────────────────────
export const FORM_MODES = {
    SIMPLE: {
        value: 'simple',
        label: 'Simple',
        description: 'All sections on one page',
    },
    ADVANCED: {
        value: 'advanced',
        label: 'Advanced (Step-wise)',
        description: 'Sections grouped into steps with navigation',
    },
};

export const CONSENT_SECTION_TYPES = {
    BRAND_IMAGE: 'brand_image',
    PATIENT_INFO: 'patient_info',
    PARAGRAPH: 'paragraph',
    TREATMENT_INFO: 'treatment_info',
    RISKS_AND_BENEFITS: 'risks_benefits',
    CHECKBOX_CONSENT: 'checkbox_consent',
    PHOTO_CONSENT: 'photo_consent',
    FINANCIAL_CONSENT: 'financial',
    SIGNATURE: 'signature',
    DECLARATION: 'declaration',
    CUSTOM: 'custom',
};

export const CONSENT_SECTION_TYPE_LABELS = {
    [CONSENT_SECTION_TYPES.BRAND_IMAGE]: 'Brand Image',
    [CONSENT_SECTION_TYPES.PATIENT_INFO]: 'Patient Info',
    [CONSENT_SECTION_TYPES.PARAGRAPH]: 'Paragraph',
    [CONSENT_SECTION_TYPES.TREATMENT_INFO]: 'Treatment Info',
    [CONSENT_SECTION_TYPES.RISKS_AND_BENEFITS]: 'Risks & Benefits',
    [CONSENT_SECTION_TYPES.CHECKBOX_CONSENT]: 'Consent Checkboxes',
    [CONSENT_SECTION_TYPES.PHOTO_CONSENT]: 'Photo Consent',
    [CONSENT_SECTION_TYPES.FINANCIAL_CONSENT]: 'Financial Consent',
    [CONSENT_SECTION_TYPES.SIGNATURE]: 'Signature',
    [CONSENT_SECTION_TYPES.DECLARATION]: 'Declaration',
    [CONSENT_SECTION_TYPES.CUSTOM]: 'Custom Section',
};

export const CONSENT_FIELD_TYPES = {
    TEXT: 'text',
    TEXTAREA: 'textarea',
    PARAGRAPH_DISPLAY: 'paragraph_display',
    CHECKBOX: 'checkbox',
    CHECKBOX_GROUP: 'checkbox_group',
    RADIO: 'radio',
    DATE: 'date',
    DATE_DROPDOWNS: 'date_dropdowns',
    SIGNATURE: 'signature',
    IMAGE: 'image',
    SELECT: 'select',
    EMAIL: 'email',
    PHONE: 'phone',
    NUMBER: 'number',
    HEADING: 'heading',
    DIVIDER: 'divider',
    HTML: 'html',
    // ── New interactive field types ──
    TOGGLE_ACK: 'toggle_ack',
    TOGGLE_DETAIL: 'toggle_detail',
    TOGGLE_TEXT: 'toggle_text',
    PILL_SELECT: 'pill_select',
    LIKERT_SCALE: 'likert_scale',
    NPS_SCALE: 'nps_scale',
};

export const CONSENT_FIELD_TYPE_LABELS = {
    [CONSENT_FIELD_TYPES.TEXT]: 'Text',
    [CONSENT_FIELD_TYPES.TEXTAREA]: 'Text Area',
    [CONSENT_FIELD_TYPES.PARAGRAPH_DISPLAY]: 'Paragraph (Display)',
    [CONSENT_FIELD_TYPES.CHECKBOX]: 'Checkbox',
    [CONSENT_FIELD_TYPES.CHECKBOX_GROUP]: 'Checkbox Group',
    [CONSENT_FIELD_TYPES.RADIO]: 'Radio Buttons',
    [CONSENT_FIELD_TYPES.DATE]: 'Date',
    [CONSENT_FIELD_TYPES.DATE_DROPDOWNS]: 'Date (Dropdowns)',
    [CONSENT_FIELD_TYPES.SIGNATURE]: 'Signature',
    [CONSENT_FIELD_TYPES.IMAGE]: 'Image',
    [CONSENT_FIELD_TYPES.SELECT]: 'Dropdown',
    [CONSENT_FIELD_TYPES.EMAIL]: 'Email',
    [CONSENT_FIELD_TYPES.PHONE]: 'Phone',
    [CONSENT_FIELD_TYPES.NUMBER]: 'Number',
    [CONSENT_FIELD_TYPES.HEADING]: 'Heading',
    [CONSENT_FIELD_TYPES.DIVIDER]: 'Divider',
    [CONSENT_FIELD_TYPES.HTML]: 'HTML Content',
    [CONSENT_FIELD_TYPES.TOGGLE_ACK]: 'Toggle Acknowledgment',
    [CONSENT_FIELD_TYPES.TOGGLE_DETAIL]: 'Toggle with Details',
    [CONSENT_FIELD_TYPES.TOGGLE_TEXT]: 'Toggle with Text',
    [CONSENT_FIELD_TYPES.PILL_SELECT]: 'Pill Select',
    [CONSENT_FIELD_TYPES.LIKERT_SCALE]: 'Rating Scale',
    [CONSENT_FIELD_TYPES.NPS_SCALE]: 'NPS Scale',
};

export const LEAD_PROPERTY_OPTIONS = [
    // Patient Info
    { value: 'salutation', label: 'Salutation / Title', group: 'Patient Info' },
    { value: 'first_name', label: 'First Name', group: 'Patient Info' },
    { value: 'last_name', label: 'Last Name', group: 'Patient Info' },
    { value: 'email', label: 'Email', group: 'Patient Info' },
    { value: 'phone_number', label: 'Phone Number', group: 'Patient Info' },
    { value: 'date_of_birth', label: 'Date of Birth', group: 'Patient Info' },
    { value: 'address', label: 'Address', group: 'Patient Info' },
    { value: 'city', label: 'City', group: 'Patient Info' },
    { value: 'postcode', label: 'Postcode', group: 'Patient Info' },
    // Treatment Info
    { value: 'lead_treatments.treatment_item.name', label: 'Treatment Name', group: 'Treatment Info' },
    { value: 'lead_treatments.custom_price', label: 'Treatment Price', group: 'Treatment Info' },
    // Practitioner Info
    { value: 'assignees.first_name', label: 'Practitioner First Name', group: 'Practitioner Info' },
    { value: 'assignees.last_name', label: 'Practitioner Last Name', group: 'Practitioner Info' },
    // Other
    { value: 'referral', label: 'Referral Source', group: 'Other' },
    { value: 'created_at', label: 'Lead Created Date', group: 'Other' },
    { value: 'tags', label: 'Tags', group: 'Other' },
];

export const SYSTEM_AUTO_FILL_OPTIONS = [
    { value: 'today_date', label: "Today's Date", icon: 'mdi:calendar-today', types: ['date', 'text'] },
    { value: 'current_time', label: 'Current Time', icon: 'mdi:clock-outline', types: ['text'] },
    { value: 'current_datetime', label: 'Current Date & Time', icon: 'mdi:calendar-clock', types: ['text'] },
    { value: 'practice_name', label: 'Practice Name', icon: 'mdi:hospital-building', types: ['text'] },
    { value: 'form_name', label: 'Form Name', icon: 'mdi:file-document-outline', types: ['text'] },
];

export const createField = (overrides = {}) => ({
    id: uuidv4(),
    label: '',
    type: CONSENT_FIELD_TYPES.TEXT,
    required: false,
    placeholder: '',
    helper_text: '',
    default_value: '',
    lead_mapping: null,
    options: [],
    validation: {},
    order: 0,
    conditional_logic: null,
    metadata: {},
    ...overrides,
});

export const createSection = (overrides = {}) => ({
    id: uuidv4(),
    name: '',
    section_type: CONSENT_SECTION_TYPES.CUSTOM,
    description: '',
    order: 0,
    fields: [],
    condition: null,
    ...overrides,
});

export const getDefaultSections = () => [
    createSection({
        name: 'Brand Image',
        section_type: CONSENT_SECTION_TYPES.BRAND_IMAGE,
        order: 0,
        fields: [
            createField({
                label: 'Brand Logo',
                type: CONSENT_FIELD_TYPES.IMAGE,
                required: false,
                order: 0,
            }),
        ],
    }),
    createSection({
        name: 'Patient Information',
        section_type: CONSENT_SECTION_TYPES.PATIENT_INFO,
        order: 1,
        fields: [
            createField({ label: 'Patient Name', type: CONSENT_FIELD_TYPES.TEXT, required: true, lead_mapping: 'first_name', placeholder: 'Enter your name', order: 0 }),
            createField({ label: 'Email', type: CONSENT_FIELD_TYPES.EMAIL, required: true, lead_mapping: 'email', placeholder: 'Enter your email', order: 1 }),
            createField({ label: 'Phone', type: CONSENT_FIELD_TYPES.PHONE, required: false, lead_mapping: 'phone_number', placeholder: 'Enter your phone number', order: 2 }),
            createField({ label: 'Date of Birth', type: CONSENT_FIELD_TYPES.DATE, required: false, lead_mapping: 'date_of_birth', order: 3 }),
            createField({ label: 'Address', type: CONSENT_FIELD_TYPES.TEXTAREA, required: false, lead_mapping: 'address', placeholder: 'Enter your address', order: 4 }),
        ],
    }),
    createSection({
        name: 'Declaration',
        section_type: CONSENT_SECTION_TYPES.DECLARATION,
        order: 98,
        fields: [
            createField({ label: 'Declaration', type: CONSENT_FIELD_TYPES.PARAGRAPH_DISPLAY, default_value: 'I confirm that I have read and understood all the information provided.', order: 0 }),
            createField({ label: 'I confirm I have read and understood all information provided', type: CONSENT_FIELD_TYPES.CHECKBOX, required: true, order: 1 }),
        ],
    }),
    createSection({
        name: 'Signature',
        section_type: CONSENT_SECTION_TYPES.SIGNATURE,
        order: 99,
        fields: [
            createField({ label: 'Patient Signature', type: CONSENT_FIELD_TYPES.SIGNATURE, required: true, metadata: { allow_typed: true, allow_drawn: true }, order: 0 }),
            createField({ label: 'Date Signed', type: CONSENT_FIELD_TYPES.DATE, required: true, order: 1 }),
            createField({ label: 'Witness Name', type: CONSENT_FIELD_TYPES.TEXT, required: false, placeholder: 'Witness name (optional)', order: 2 }),
            createField({ label: 'Witness Signature', type: CONSENT_FIELD_TYPES.SIGNATURE, required: false, metadata: { allow_typed: true, allow_drawn: true }, order: 3 }),
        ],
    }),
];
